/**
 * "Create with AI" — a contextual entry to the agentic Otto composer, placed
 * next to the plain Create button on the invoice / quote / campaign pages. It
 * opens the same Otto command dialog the ⌘K launcher uses, PRE-SCOPED to the
 * given skill (so the input is seeded with that skill's example).
 *
 * Renders NOTHING when the signed-in user can't run this skill (e.g. no
 * `crm:financials` ⇒ no invoice/quote button) — the same permission gate as the
 * launcher, so the affordance never appears where it wouldn't work.
 *
 * Lives with the agentic feature; the CRM pages import it relatively. It relies
 * on the app-wide `AgenticProvider` (mounted in AppShell), so it works from
 * anywhere in the authed tree, including the walled CRM sub-app.
 */
import { Button, type ButtonProps } from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import type { JSX } from 'react';

import type { OttoAgentSkillId } from '@xop/dashboard-shared';

import { useAgenticOptional } from './AgenticProvider.js';
import { useAgenticEnabled } from './useAgenticEnabled.js';

export interface CreateWithAiButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  skill: OttoAgentSkillId;
  /** Button label (default "Create with AI"). */
  label?: string;
}

export function CreateWithAiButton({
  skill,
  label = 'Create with AI',
  ...buttonProps
}: CreateWithAiButtonProps): JSX.Element | null {
  const { skills } = useAgenticEnabled();
  const agentic = useAgenticOptional();

  // No provider (isolated render) or the user can't run this skill → render nothing.
  if (!agentic || !skills.includes(skill)) return null;

  return (
    <Button
      variant="contained"
      startIcon={<AutoAwesomeRoundedIcon />}
      onClick={() => agentic.openComposer(skill)}
      data-testid={`create-with-ai-${skill}`}
      {...buttonProps}
      sx={{
        // A premium navy→blue gradient — distinct from the plain "Create X"
        // button and readable (white text on both stops), unlike the pale lime.
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.info.main})`,
        color: (t) => t.palette.primary.contrastText,
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': { boxShadow: 2, filter: 'brightness(1.06)' },
        ...(buttonProps.sx ?? {}),
      }}
    >
      {label}
    </Button>
  );
}
